import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPanelCard, type PanelCardView } from '../../api/admin';
import type { Meta } from '../../api/meta';
import { ca } from '../../i18n/ca';
import { fmtWhen } from './shared';

/**
 * The panel as the platform sees it: whether it answers, who the platform is to it, which
 * servers it holds and what went wrong the last few times. The platform asks the panel; when
 * the panel says nothing, the card says «el panell no respon» with the last time it did, and the
 * rest of the page is untouched (a failed read of the card is a line on the card, never on the page).
 */
export function PanelCard({ meta, counts }: { meta: Meta; counts: string | null }) {
  const t = ca.adminUi.panelDetail;
  const [card, setCard] = useState<PanelCardView | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(true);

  const load = useCallback(() => {
    setBusy(true);
    fetchPanelCard()
      .then((view) => {
        setCard(view);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  }, []);

  useEffect(load, [load]);

  const configured = card ? card.configured : meta.panelConfigured;
  const tone = !configured ? 'warn' : card?.reachable ? 'ok' : card ? 'bad' : 'warn';
  const headline = !configured
    ? ca.panelNotConfigured
    : card === null
      ? failed
        ? t.unavailable
        : t.checking
      : card.reachable
        ? t.answers
        : t.silent;

  return (
    <section className="card" aria-label={ca.adminUi.panelCard}>
      <h2>
        <i className="bi bi-hdd-stack" aria-hidden="true" /> {ca.adminUi.panelCard}
      </h2>
      <p className="muted small">{ca.adminUi.panelCardIntro}</p>
      <p className="row">
        <span className="muted small">{ca.adminPage.panelStatus}:</span>
        <span className={`chip ${tone}`} role="status">
          {headline}
        </span>
      </p>
      {card && card.configured && (
        <dl className="facts">
          <dt>{t.lastAnswered}</dt>
          <dd>{card.lastAnsweredAt ? fmtWhen(card.lastAnsweredAt) : t.neverAnswered}</dd>
          <dt>{t.account}</dt>
          <dd>
            {card.serviceAccount ?? t.accountUnknown}
            <span className="muted small"> · {t.credentials[card.authMode]}</span>
          </dd>
          <dt>{t.version}</dt>
          <dd className="muted">{t.versionUnknown}</dd>
          <dt>{t.servers}</dt>
          <dd>
            {card.servers.length === 0 ? (
              <span className="muted">{card.reachable ? t.noServers : t.serversUnknown}</span>
            ) : (
              <ul className="plain">
                {card.servers.map((s) => (
                  <li key={s.id}>
                    {s.name}{' '}
                    <span className="muted small">
                      · {t.access[s.access]}
                      {s.createdBy ? ` · ${s.createdBy}` : ` · ${t.operators}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {counts && <span className="muted small">{counts}</span>}
          </dd>
          <dt>{t.errors}</dt>
          <dd>
            {card.recentFailures.length === 0 ? (
              <span className="muted">{t.noErrors}</span>
            ) : (
              <ul className="plain">
                {card.recentFailures.map((f) => (
                  <li key={`${f.at}-${f.reason}`}>
                    <span className="muted small">{fmtWhen(f.at)}</span> {f.reason}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </dl>
      )}
      <div className="row actions">
        <button type="button" className="ghost" onClick={load} disabled={busy}>
          <i className="bi bi-arrow-repeat" aria-hidden="true" /> {t.recheck}
        </button>
        {meta.panelHost && (
          <a
            className="button"
            href={`https://${meta.panelHost}/`}
            target="_blank"
            rel="noreferrer"
          >
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {ca.adminPage.openPanel}
          </a>
        )}
        <Link className="button ghost" to="/reports">
          <i className="bi bi-file-earmark-text" aria-hidden="true" />{' '}
          {ca.adminPage.panelReportsShortcut}
        </Link>
      </div>
    </section>
  );
}
